import { captureException } from '@sentry/nextjs';
import { useQuery } from '@tanstack/react-query';
import { createContext, ReactNode, use, useEffect, useMemo } from 'react';

import { getCityzensProfile } from 'services/account';
import { getLocalizationDictionary } from 'services/cityzens';

import { useBrowsing } from 'hooks/use-browsing';

import { useUserStore } from 'stores/use-user-store';

import { queryKeys } from '@feed/queries/keys';

import { resolveLang } from '../utils/helpers';
import { type ProfileData, useSessionStore } from './store';

export type { ProfileData };

type SessionValue = {
	state: {
		profileData: ProfileData | null;
		isUserAuthenticated: boolean;
		initialLoad: boolean;
		displayLang: string;
	};
	actions: {
		getDictionaryItem: (filterItem: string) => string;
	};
	meta: {
		assetsApiUrl: string;
	};
};

const SessionContext = createContext<SessionValue | null>(null);

type SessionProviderProps = {
	children?: ReactNode;
	assetsApiUrl: string;
};

export function SessionProvider({
	assetsApiUrl,
	children,
}: SessionProviderProps) {
	const { query, isReady } = useBrowsing();
	const setLocalizationData = useSessionStore(
		(state) => state.setLocalizationData
	);
	const setProfileData = useSessionStore((state) => state.setProfileData);
	const setDisplayLang = useSessionStore((state) => state.setDisplayLang);
	const setUser = useUserStore((state) => state.setUser);

	const profileData = useSessionStore((state) => state.profileData);
	const displayLang = useSessionStore((state) => state.displayLang);
	const getDictionaryItem = useSessionStore(
		(state) => state.getDictionaryItem
	);

	const dictionaryQuery = useQuery({
		queryKey: queryKeys.session.dictionary(assetsApiUrl),
		queryFn: async () => {
			try {
				return await getLocalizationDictionary({ assetsApiUrl });
			} catch (err) {
				captureException(err, {
					tags: {
						api: 'getLocalizationData',
					},
				});
				return null;
			}
		},
		enabled: Boolean(assetsApiUrl),
	});

	const profileQuery = useQuery({
		queryKey: queryKeys.session.profile(),
		queryFn: getCityzensProfile,
	});

	// Derived from the query rather than mirrored into state: a widget
	// double-writing profileQuery's status into zustand would only add a
	// render between the query settling and the store reflecting it.
	const isUserAuthenticated = Boolean(profileQuery.data);
	const initialLoad = profileQuery.isPending;

	useEffect(() => {
		const data = dictionaryQuery.data;
		if (!data || Object.values(data).length === 0) {
			return;
		}
		if (data.global) {
			setLocalizationData(data.global);
		}
	}, [dictionaryQuery.data, setLocalizationData]);

	useEffect(() => {
		if (profileQuery.isPending) {
			return;
		}

		const data = profileQuery.data;
		if (data) {
			setProfileData({
				id: data.id,
				firstName: data.firstName,
				lastName: data.lastName,
				supporterNumber: data.supporterNumber ?? 0,
				membership: String(data.membership ?? ''),
				ticketPoints: data.ticketPoints ?? 0,
				lang: data.lang,
				avatar: data.avatar,
			});
			setUser(data);
		}
	}, [profileQuery.data, profileQuery.isPending, setProfileData, setUser]);

	// Single source of truth for displayLang: an explicit `?lang=` wins (a fan
	// following a shared link should see that language), otherwise fall back
	// to the profile's saved language once it loads. Previously these were
	// two separate effects that could race and overwrite one another.
	useEffect(() => {
		if (!isReady) {
			return;
		}

		if (query.lang) {
			const lang = resolveLang(query.lang);
			setDisplayLang(lang);
			document.getElementsByTagName('html')[0].setAttribute('lang', lang);
			return;
		}

		if (profileQuery.data?.lang) {
			setDisplayLang(profileQuery.data.lang);
		}
	}, [isReady, query.lang, profileQuery.data?.lang, setDisplayLang]);

	const value = useMemo<SessionValue>(
		() => ({
			state: {
				profileData,
				isUserAuthenticated,
				initialLoad,
				displayLang,
			},
			actions: {
				getDictionaryItem,
			},
			meta: {
				assetsApiUrl,
			},
		}),
		[
			profileData,
			isUserAuthenticated,
			initialLoad,
			displayLang,
			getDictionaryItem,
			assetsApiUrl,
		]
	);

	return (
		<SessionContext.Provider value={value}>
			{children}
		</SessionContext.Provider>
	);
}

export function useSession() {
	const value = use(SessionContext);
	if (!value) {
		throw new Error('useSession must be used within SessionProvider');
	}
	return value;
}

/** Fan's authenticated profile, or null while loading / for a guest. */
export function useProfileData() {
	return useSession().state.profileData;
}

/** True once the profile query has resolved to an authenticated fan. */
export function useIsUserAuthenticated() {
	return useSession().state.isUserAuthenticated;
}

/** True while the initial profile fetch is still in flight. */
export function useInitialLoad() {
	return useSession().state.initialLoad;
}

/** Active display language (`?lang=` > profile language > default). */
export function useDisplayLang() {
	return useSession().state.displayLang;
}

/** Looks up a copy string from the assets dictionary for the active language. */
export function useDictionaryItem() {
	const getDictionaryItem = useSession().actions.getDictionaryItem;
	return getDictionaryItem;
}

/**
 * Compatibility hook used by existing widget screens. Prefer the narrow
 * hooks above (`useProfileData`, `useIsUserAuthenticated`, …) in new code —
 * this one re-renders on any session field change since it reads all of them.
 */
export function useLocalizationContext() {
	const session = useSession();
	const localizationData = useSessionStore((state) => state.localizationData);

	return useMemo(
		() => ({
			localizationData,
			profileData: session.state.profileData,
			isUserAuthenticated: session.state.isUserAuthenticated,
			initialLoad: session.state.initialLoad,
			getDictionaryItem: session.actions.getDictionaryItem,
			displayLang: session.state.displayLang,
		}),
		[localizationData, session]
	);
}
